const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// Get stored token
const getToken = () => localStorage.getItem('token');

// API request helper
async function request(endpoint, options = {}) {
  const token = getToken();

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// Auth API
export const authAPI = {
  register: (email, password, name) =>
    request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),

  login: (email, password) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request('/auth/me'),
};

// Progress API
export const progressAPI = {
  getAll: () => request('/progress'),

  getPart: (partNumber) => request(`/progress/part/${partNumber}`),

  saveAnswer: (partNumber, questionId, selectedAnswer, isCorrect) =>
    request('/progress/answer', {
      method: 'POST',
      body: JSON.stringify({ partNumber, questionId, selectedAnswer, isCorrect }),
    }),

  completePart: (partNumber, score, totalQuestions) =>
    request('/progress/complete', {
      method: 'POST',
      body: JSON.stringify({ partNumber, score, totalQuestions }),
    }),
};

// Reports API
export const reportsAPI = {
  getAll: () => request('/reports'),

  submit: (questionId, partNumber, questionStem, comment) =>
    request('/reports', {
      method: 'POST',
      body: JSON.stringify({ questionId, partNumber, questionStem, comment }),
    }),

  resolve: (id) =>
    request(`/reports/${id}/resolve`, { method: 'PATCH' }),

  delete: (id) =>
    request(`/reports/${id}`, { method: 'DELETE' }),

  deleteAll: () =>
    request('/reports', { method: 'DELETE' }),
};

// Analytics API
export const analyticsAPI = {
  getStats: () => request('/analytics'),
};

// Bookmarks API
export const bookmarksAPI = {
  getAll: () => request('/bookmarks'),

  add: (questionId, partNumber) =>
    request('/bookmarks', {
      method: 'POST',
      body: JSON.stringify({ questionId, partNumber }),
    }),

  remove: (questionId) =>
    request(`/bookmarks/${questionId}`, { method: 'DELETE' }),
};

// Notes API
export const notesAPI = {
  getAll: () => request('/notes'),

  get: (questionId) => request(`/notes/${questionId}`),

  save: (questionId, content) =>
    request(`/notes/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  delete: (questionId) =>
    request(`/notes/${questionId}`, { method: 'DELETE' }),
};

// Strikethroughs API
export const strikethroughsAPI = {
  getAll: () => request('/strikethroughs'),

  add: (questionId, optionIndex) =>
    request('/strikethroughs', {
      method: 'POST',
      body: JSON.stringify({ questionId, optionIndex }),
    }),

  remove: (questionId, optionIndex) =>
    request(`/strikethroughs/${questionId}/${optionIndex}`, {
      method: 'DELETE',
    }),

  clearQuestion: (questionId) =>
    request(`/strikethroughs/${questionId}`, { method: 'DELETE' }),
};

// Quiz Sessions API
export const sessionsAPI = {
  create: (mode, questionIds, timeLimit = null) =>
    request('/sessions', {
      method: 'POST',
      body: JSON.stringify({ mode, questionIds, timeLimit }),
    }),

  get: (sessionId) => request(`/sessions/${sessionId}`),

  getAll: () => request('/sessions'),

  updateProgress: (sessionId, currentIndex) =>
    request(`/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ currentIndex }),
    }),

  complete: (sessionId) =>
    request(`/sessions/${sessionId}/complete`, { method: 'POST' }),
};

// Question Times API
export const timesAPI = {
  save: (questionId, timeSeconds) =>
    request('/times', {
      method: 'POST',
      body: JSON.stringify({ questionId, timeSeconds }),
    }),

  getAll: () => request('/times'),
};
